import { useCallback, useEffect, useState } from 'react';
import {
  clearApiToken,
  getApiToken,
  setApiToken,
  verifyApiToken,
} from './api/auth';

const API_BASE_HINT =
  (process.env.REACT_APP_API_BASE || '').trim() ||
  (typeof window !== 'undefined' ? window.location.origin : '');

function ApiTokenBar() {
  const [draft, setDraft] = useState(() => getApiToken());
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [collapsed, setCollapsed] = useState(() => Boolean(getApiToken()));

  const runVerify = useCallback(async (tokenValue) => {
    const value = (tokenValue ?? draft).trim();
    if (!value) {
      setStatus('missing');
      setMessage('Paste the same EXPECTED_TOKEN as pricing /ws/demo');
      return false;
    }

    setStatus('checking');
    setMessage('Checking token…');
    try {
      await verifyApiToken(value);
      setStatus('valid');
      setMessage('Token accepted — dashboard will connect');
      return true;
    } catch (err) {
      setStatus('invalid');
      setMessage(err.message || 'Invalid token');
      return false;
    }
  }, [draft]);

  const applyToken = useCallback(async () => {
    const value = draft.trim();
    if (!value) {
      clearApiToken();
      setStatus('missing');
      setMessage('Token cleared');
      return;
    }

    const ok = await runVerify(value);
    if (ok) {
      setApiToken(value);
      setCollapsed(true);
    }
  }, [draft, runVerify]);

  useEffect(() => {
    const existing = getApiToken();
    if (!existing) return;
    setDraft(existing);
    runVerify(existing);
  }, [runVerify]);

  return (
    <div className={`api-token-bar${collapsed ? ' api-token-bar--collapsed' : ''}`}>
      <div className="api-token-bar__head">
        <span className="api-token-bar__title">API token</span>
        <span className="api-token-bar__hint">
          Same as <code>EXPECTED_TOKEN</code> on pricing FastAPI ({API_BASE_HINT || 'proxy → :8585'})
        </span>
        <button
          type="button"
          className="api-token-bar__toggle"
          onClick={() => setCollapsed((c) => !c)}
        >
          {collapsed ? 'Change' : 'Hide'}
        </button>
      </div>

      {!collapsed && (
        <div className="api-token-bar__row">
          <input
            type="password"
            className="api-token-bar__input"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              if (status !== 'idle') setStatus('idle');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyToken();
            }}
            placeholder="GodoFintech… (not UAT JWT)"
            autoComplete="off"
            spellCheck={false}
          />
          <button type="button" className="api-token-bar__btn" onClick={applyToken}>
            Apply & connect
          </button>
          <button
            type="button"
            className="api-token-bar__btn api-token-bar__btn--ghost"
            onClick={() => runVerify()}
          >
            Test only
          </button>
        </div>
      )}

      {message && (
        <p className={`api-token-bar__status api-token-bar__status--${status}`}>
          {message}
        </p>
      )}
    </div>
  );
}

export default ApiTokenBar;
